import React, { useEffect } from 'react';
import { Col } from '@gjensidige/core-grid';
import { Title, TitleProps, Text, Label, Input } from '@gjensidige/builders-components';
import { useAppDispatch, useAppSelector } from '../hooks/appHooks';
import { ROUTES } from '../lib/constants';
import { PRODUCTS } from '../lib/constants/appConstants';
import { AppConstants } from '../utils/AppConstants';
import { updateInsurancePackageSelection } from '../store/slices/offerSlice';
import { updatePurchaseStatus } from '../store/slices/offerSlice';
import tracker from '../tracking';
import { ComparisonTableDesktop } from '../components/InsurancePackage/ComparisonTableDesktop';
import InsurancePackageSmallScreen from '../components/InsurancePackage/InsurancePackageSmallScreen';
import { Package, ColumnHeaderProps, RowProps } from '../interfaces/Types';
import { ConfigHelper } from '../utils/ConfigHelper';
import { InsuranceHelper } from '../utils/InsuranceHelper';
import { TextHelper } from '../utils/TextHelper';
import { TrackingHelper } from '../utils/TrackingHelper';
import styles from './styles/OfferTableDesktop.module.scss';
import { OfferBadge } from '../components/InsurancePackage/OfferBadge';

export const InsurancePackageContainer: React.FC = () => {
  const packages = useAppSelector((state) =>
    state.insurance.packages.filter((item: Package) => !item.isPlusPackage)
  );
  const { productName, productCode, policyNumber } = useAppSelector(
    (state) => state.insurance.offer
  );
  const channel = InsuranceHelper.getChannelInfo();
  const dispatch = useAppDispatch();
  const selectedPackage = InsuranceHelper.getSelectedPackageFromConfig(packages);

  useEffect(() => {
    const stepStatus = TextHelper.getHeaderStepLabels(
      productName,
      ROUTES.offerPage
    );
    dispatch(updatePurchaseStatus(stepStatus));
    if (packages.length > 0) {
      const trackOptions = {
        event_name: TrackingHelper.getEventNameToTrackOnOfferLoad(productName),
        product_name: TrackingHelper.getProductListToTrack(productName),
        product_brand: ['insurance'],
        product_category: [
          TrackingHelper.getProductCategoryToTrack(productName)
        ],
        product_sku: [productCode],
        scenario_name: TrackingHelper.getScenarioNameFromProduct(productName),
        scenario_step: 'choose-plan',
        scenario_type: TrackingHelper.isFollowUpOffer(policyNumber) ? 'follow-up-offer' : ''
      };
      tracker.setOptions(trackOptions);
      tracker.trackScenerioStep(
        'offer',
        TrackingHelper.getScenarioNameFromProduct(productName),
        'choose-plan'
      );
    }
  }, []);

  const campaignDiscountPercentage = ConfigHelper.getCampaignDiscountPercentages(
    productName,
    channel.toLowerCase()
  );

  const updatePackageSelection = (packageCode: string) => {
    if (packageCode) {
      const selectedPackage = packages.find(
        (packageItem: Package) => packageItem.packageCode === packageCode
      );
      if (selectedPackage) {
        dispatch(updateInsurancePackageSelection(selectedPackage));
      }
    }
  };

  const getHeaderTabInfo = () => {
    if (selectedPackage && selectedPackage[0]) {
      return TextHelper.getSmallScreenPackageHeaderLabels(
        productName,
        selectedPackage[0],
        campaignDiscountPercentage
      );
    }
    return undefined;
  };

  const mapToTableDesktopContent = (
    packages: Package[]
  ): {
    header: ColumnHeaderProps;
    footer: React.ReactNode;
  }[] =>
    packages
      .filter((packageObj) => !packageObj.isPlusPackage)
      .map((packageObj) => {
        const isDisabled = !packageObj.isEnabled;
        const priceHeaderInfo = TextHelper.getPackageHeaderLabels(
          productName,
          packageObj,
          campaignDiscountPercentage
        );
        const header = {
          id: packageObj.packageCode,
          label: packageObj.packageName ?? "",
          isHighlighted: packageObj.isSelected,
          isDisabled: isDisabled,
          disabledMessage: packageObj.disabledMessage || "",
          content: () => (
            <>
              {packageObj.defaultPackage && packageObj.defaultPackageMessage && (
                <OfferBadge
                  className={styles.badge}
                  modalMessage={packageObj.defaultPackageMessage}
                  modalHeading={AppConstants.getAppText(
                    'webshop.insurance.recommendedInfoLink'
                  )}
                  linkText={AppConstants.getAppText(
                    'webshop.insuranceRecommendedLabel'
                  )}
                />
              )}
              <Title as={"h3" as TitleProps['as']} size="5" className={styles.headerTitle}>
                {packageObj?.packageName ?? ""}
              </Title>
              <>
                <div className={styles.headerPriceWrapper}>
                  <Text size="lead" weight="700">
                    {packageObj.monthlyPrice ?
                      `${TextHelper.thousandSeparator(packageObj.monthlyPrice)} kr` : '-'}
                  </Text>
                  {packageObj.monthlyPrice && <Text className={styles.priceAndMonth}>
                    /mån
                  </Text>}

                </div>
                {packageObj.monthlyPrice && priceHeaderInfo.oldPriceLabel && (
                  <Text size="body" className={styles.oldPrice}>
                    {priceHeaderInfo.oldPriceLabel}
                  </Text>
                )}
                {priceHeaderInfo.packageInfos?.map((info: string, index: number) => {
                  if (info) {
                    return (
                      <Text
                        size="small"
                        className="additional-info-label pakage-header-label"
                        key={index}
                      >
                        {info}
                      </Text>
                    );
                  }
                  return null;
                })}
                {
                  packageObj.detailText &&
                  <Text
                    className={styles.headerDetailText}
                    size="small"
                    dangerouslySetInnerHTML={{
                      __html: packageObj.detailText
                    }}
                  />
                }
              </>
            </>
          ),
        };

        const footer = (
          <Label className={styles.footer} htmlFor={`${packageObj.packageCode}`}>

            <Input
              type="radio"
              key={`${packageObj.packageCode}`}
              id={`${packageObj.packageCode}`}
              aria-label={packageObj?.packageCode}
              value={`${packageObj.packageCode}`}
              name={`${packageObj.packageCode}`}
              data-theme={packageObj.isSelected ? "dark" : "light"}
              checked={packageObj.isSelected}
              disabled={isDisabled}
              tracking={false}
            />

            {"Välj " + packageObj?.packageName}
          </Label>
        );

        return {
          header,
          footer,
        };
      });

  const mapToTableRows = (packages: Package[]): RowProps[] => {
    const rows: RowProps[] = [];
    packages
      .filter((packageObj) => !packageObj.isPlusPackage)
      .map((packageObj) => {
        packageObj.includedCoverages.coverageList.map((coverage) => {
          const isPresent = rows.find((row) => row.id === coverage.name);
          if (!isPresent) {
            rows.push({
              id: coverage.name,
              label: coverage.name,
              expandableContent: coverage.description,
              columns: InsuranceHelper.getCoverageColumns(packages, coverage.name),
            });
          }
        });
      });
    return rows;
  };

  const tableColumns = mapToTableDesktopContent(packages);
  const tableRows = mapToTableRows(packages);

  const onPackageSelection = (packageCode: string) => {
    if (packageCode) {
      const selectedpacakge = packages.find(
        (packageItem: Package) => packageItem.packageCode === packageCode
      );
      const currentPackage = packages.find(
        (packageItem: Package) => packageItem.isSelected
      );
      if (selectedpacakge?.packageCode !== currentPackage?.packageCode) {
        tracker.trackEvent(
          {
            eventAction: 'checked',
            eventCategory: 'input',
            eventLabel: `välj ${selectedpacakge?.packageName}`
          }
        );
        updatePackageSelection(packageCode);
      }
    }
  }

  return (
    <>
      {packages.length > 0 && (
        <>
          {/* //Insurence packages for large screen devices */}
          <section
            className={`d-none d-xl-block mb-1`}
          >
            <ComparisonTableDesktop
              id={productName}
              isTouchingStepIndicator={false}
              hasBadge={false}
              columnHeaders={tableColumns.map((column) => column.header)}
              columnFooters={tableColumns.map((column) => column.footer)}
              rows={tableRows}
              productName={productName}
              onColumnClick={(columnIndex: string) => onPackageSelection(columnIndex)}
            />
          </section>
          {/* //Insurence packages for small screen devices */}
          <Col
            md={{ span: 8, offset: 2 }}
            lg={{ span: 8, offset: 2 }}
            xl={{ span: 6, offset: 3 }}
            sm="12"
            className={`d-block d-xl-none mb-4 ${productName === PRODUCTS.CAR ? 'mt-2' : ''
              }`}
          >
            <InsurancePackageSmallScreen
              selectionTabs={InsuranceHelper.getSmallScreenTabs(packages)}
              updateTileSelection={updatePackageSelection}
              header={getHeaderTabInfo()}
              isPackageSelected={
                selectedPackage && selectedPackage[0]?.packageCode
              }
              includedCoverages={
                selectedPackage[0]?.includedCoverages?.coverageList || []
              }
              excludedCoverages={
                selectedPackage[0]?.excludedCoverages?.coverageList || []
              }
              disabledMessage={selectedPackage[0]?.disabledMessage}
              isEnabled={selectedPackage[0]?.isEnabled}
            />
          </Col>
        </>
      )}
    </>
  );
};
